import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";

const STATUS_STEPS_DELIVERY = [
  ["received", "Order received"],
  ["confirmed", "Confirmed"],
  ["preparing", "Preparing"],
  ["ready", "Ready"],
  ["out_for_delivery", "Out for delivery"],
  ["delivered", "Delivered"],
];
const STATUS_STEPS_PICKUP = [
  ["received", "Order received"],
  ["confirmed", "Confirmed"],
  ["preparing", "Preparing"],
  ["ready_pickup", "Ready for pickup"],
  ["collected", "Collected"],
];

// While a rider is in-flight we want the page to feel alive without a manual
// refresh — poll every 20s until the order lands. Any terminal status ends
// the polling so we don't hammer the backend after delivery.
const IN_FLIGHT_STATUSES = new Set(["preparing", "ready", "out_for_delivery"]);
const REFRESH_MS = 20_000;

const IN = "en-IN";
const IST = { timeZone: "Asia/Kolkata" };

// "2026-02-15" (interpreted as an IST calendar date) → "Sat, 15 Feb 2026"
function fmtSlotDate(v) {
  if (!v) return "";
  // Treat the naive YYYY-MM-DD as an IST calendar day (not UTC midnight).
  const d = new Date(`${v}T00:00:00+05:30`);
  if (isNaN(d)) return v;
  return d.toLocaleDateString(IN, { ...IST, weekday: "short", day: "2-digit", month: "short", year: "numeric" });
}

// "10:00" → "10:00 AM"; passes "ASAP" through unchanged
function fmtSlotTime(v) {
  if (!v) return "";
  if (!/^\d{1,2}:\d{2}$/.test(v)) return v;
  const [h, m] = v.split(":").map(Number);
  const d = new Date();
  d.setHours(h, m, 0, 0);
  return d.toLocaleTimeString(IN, { hour: "numeric", minute: "2-digit", hour12: true });
}

// ISO datetime → "15 Feb 2026, 10:00 AM IST"
function fmtPlacedAt(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  return d.toLocaleString(IN, { ...IST, day: "2-digit", month: "short", year: "numeric", hour: "numeric", minute: "2-digit", hour12: true }) + " IST";
}

function RiderMap({ order }) {
  // Some carriers block iframe embedding via X-Frame-Options / frame-ancestors,
  // so we render the iframe optimistically AND surface a prominent "Open full
  // map" pill so the customer always has a way through.
  const url = order.carrier_tracking_url;
  const hasRider = order.carrier_rider_name && order.carrier_rider_name !== "Not Provided";
  return (
    <section className="mt-10 border border-line bg-ivory p-5 md:p-6" data-testid="track-rider-map">
      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <div>
          <div className="label-xs text-gold-hover inline-flex items-center gap-2">
            <span className="inline-block w-2 h-2 bg-gold rounded-full animate-pulse" />
            Live · {order.carrier_partner_name || "your rider"} is on the way
          </div>
          <h2 className="mt-2 font-serif text-2xl md:text-3xl text-espresso">
            Watch your tiramisù approach.
          </h2>
        </div>
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className="btn-espresso whitespace-nowrap"
          data-testid="track-rider-open-map"
        >
          Open full map →
        </a>
      </div>

      {(hasRider || order.carrier_rider_contact) && (
        <div className="mt-4 grid sm:grid-cols-3 gap-4 border-t border-line pt-4">
          <div>
            <div className="label-xs text-espresso-light">Rider</div>
            <div className="text-sm text-espresso mt-1" data-testid="track-rider-name">
              {hasRider ? order.carrier_rider_name : <span className="italic text-espresso-light">Being assigned…</span>}
            </div>
          </div>
          {order.carrier_rider_contact && order.carrier_rider_contact !== "9999999999" && (
            <div>
              <div className="label-xs text-espresso-light">Rider contact</div>
              <a
                href={`tel:${order.carrier_rider_contact}`}
                className="text-sm text-espresso link-underline mt-1 inline-block"
                data-testid="track-rider-tel"
              >
                {order.carrier_rider_contact}
              </a>
            </div>
          )}
          {order.carrier_partner_name && (
            <div>
              <div className="label-xs text-espresso-light">Partner</div>
              <div className="text-sm text-espresso mt-1">{order.carrier_partner_name}</div>
            </div>
          )}
        </div>
      )}

      <div className="mt-5 aspect-[16/11] md:aspect-[16/9] w-full bg-ivory-100 border border-line overflow-hidden">
        <iframe
          src={url}
          title="Live rider map"
          className="w-full h-full"
          allow="geolocation"
          referrerPolicy="no-referrer-when-downgrade"
          loading="lazy"
          data-testid="track-rider-iframe"
        />
      </div>
      <p className="mt-3 label-xs text-espresso-light/80">
        Map not loading? <a href={url} target="_blank" rel="noreferrer" className="link-underline">Tap here to open it in a new tab.</a>
      </p>
    </section>
  );
}


export default function OrderTracking() {
  const { orderNumber } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [input, setInput] = useState("");

  useEffect(() => {
    if (!orderNumber) return;
    let cancelled = false;
    const load = () =>
      api.get(`/orders/track/${orderNumber}`)
        .then(({ data }) => { if (!cancelled) setOrder(data); })
        .catch(() => { if (!cancelled) setOrder(null); });
    load();
    // Keep the rider info + status fresh while the order is in-flight.
    const timer = setInterval(() => {
      setOrder((cur) => {
        if (cur && !IN_FLIGHT_STATUSES.has(cur.status)) return cur;
        load();
        return cur;
      });
    }, REFRESH_MS);
    return () => { cancelled = true; clearInterval(timer); };
  }, [orderNumber]);

  if (!orderNumber) {
    return (
      <main className="mx-auto max-w-[900px] px-4 pb-28 pt-10 md:px-10 md:py-24" data-testid="track-page">
        <div className="label-xs text-espresso-light">Track order</div>
        <h1 className="mt-3 font-serif text-4xl text-espresso md:text-6xl">Where&apos;s my tiramisu?</h1>
        <div className="mt-8 flex max-w-md flex-col gap-3 sm:mt-10 sm:flex-row">
          <input
            placeholder="Order number (e.g. TCO-...)"
            className="w-full bg-transparent border-b border-line py-3 focus:outline-none focus:border-gold text-espresso"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            data-testid="track-input"
          />
          <button
            className="btn-espresso w-full sm:w-auto"
            onClick={() => input && navigate(`/track/${input.trim()}`)}
            data-testid="track-submit"
          >
            Track
          </button>
        </div>
      </main>
    );
  }

  if (!order) return <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-32 text-espresso-light">Loading…</div>;

  const steps = order.fulfillment === "pickup" ? STATUS_STEPS_PICKUP : STATUS_STEPS_DELIVERY;
  const currentIdx = steps.findIndex(([k]) => k === order.status);

  return (
    <main data-testid="track-page" className="mx-auto max-w-[900px] px-4 pb-28 pt-10 md:px-10 md:py-24">
      <div className="label-xs text-espresso-light">Order tracking</div>
      <h1 className="mt-3 font-serif text-3xl text-espresso md:text-6xl" data-testid="track-order-number">{order.order_number}</h1>
      <p className="mt-4 text-espresso-light" data-testid="track-slot">
        {order.fulfillment === "delivery" ? "Delivery" : "Pickup"} · {fmtSlotDate(order.slot_date)} · {fmtSlotTime(order.slot_time)}
      </p>
      {order.created_at && (
        <p className="mt-1 text-xs text-espresso-light/80" data-testid="track-placed-at">
          Placed {fmtPlacedAt(order.created_at)}
        </p>
      )}

      {order.fulfillment === "delivery" && order.carrier_tracking_url && order.status !== "delivered" && order.status !== "cancelled" && (
        <RiderMap order={order} />
      )}

      <div className="mt-10 space-y-0">
        {steps.map(([k, label], i) => {
          const done = i <= currentIdx;
          return (
            <div key={k} className="flex min-h-16 items-start gap-5">
              <div className="relative flex h-6 w-3 justify-center">
                <div className={`mt-1 h-3 w-3 rounded-full ${done ? "bg-gold" : "bg-line"}`} />
                {i < steps.length - 1 && <div className="absolute top-5 h-11 w-px bg-line" />}
              </div>
              <div className="pb-6">
                <div className={`font-serif text-xl ${done ? "text-espresso" : "text-espresso-light"}`}>{label}</div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-14 border-t border-line pt-8">
        <a
          href={`${process.env.REACT_APP_BACKEND_URL}/api/orders/${order.order_number}/invoice.pdf`}
          target="_blank" rel="noreferrer"
          className="label-xs link-underline text-espresso"
          data-testid="track-invoice-link"
        >
          Download invoice (PDF) →
        </a>
      </div>
    </main>
  );
}
